import { useState, useEffect, ReactNode } from "react";
import classNames from "classnames";
import useModal from "@/hooks/useModal";
import Button from "@/components/Button";
import Icon from "@/components/Icon";
import FileUpload from "@/components/FileUpload/FileUpload";

export type FileUploadModalState = "idle" | "fileSelected" | "uploading" | "complete" | "error";

interface FileUploadModalProps {
  open: boolean;
  onClose: () => void;
  onUpload: (file: File) => Promise<void>;
  title: string;
  subtitle?: string;
  description?: ReactNode;
  accept?: string;
  buttonText?: string;
  onComplete?: () => void;
  completeButtonText?: string;
}

interface FileDisplayProps {
  file: File | null;
  status: string;
  error?: string | null;
}

const FileDisplay = ({ file, status, error }: FileDisplayProps) => (
  <div
    className={classNames(
      "border border-solid w-full rounded px-4 py-4 flex flex-row items-center",
      error ? "border-sys-brand-error" : "border-sys-brand-outline"
    )}
    role="status"
    aria-label={`File ${status}`}
  >
    <div className="flex-1">
      <p className="text-sys-brand-on-surface text-body-large">{file?.name || "No file selected"}</p>
      <p className="text-sys-brand-on-surface-variant text-body-medium">{status}</p>
    </div>
    <div className="ml-4">
      <Icon name="check_circle" className="text-sys-brand-success" />
    </div>
  </div>
);

export default function FileUploadModal({
  open,
  onClose,
  onUpload,
  title,
  subtitle,
  description,
  accept = ".pdf, .docx, .doc, .md, .png, .jpg",
  buttonText = "Upload",
  onComplete,
  completeButtonText = "Complete"
}: FileUploadModalProps) {
  const { Modal } = useModal();
  const [modalState, setModalState] = useState<FileUploadModalState>("idle");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setModalState("idle");
      setSelectedFile(null);
      setError(null);
    }
  }, [open]);

  const handleFileUpload = (file: File) => {
    setSelectedFile(file);
    setModalState("fileSelected");
    setError(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setModalState("uploading");
    setError(null);

    try {
      await onUpload(selectedFile);
      if (onComplete) {
        await onComplete();
      }
      setModalState("complete");
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Upload failed";
      setError(errorMessage);
      setModalState("error");
    }
  };

  const renderContent = () => {
    switch (modalState) {
      case "idle":
        return (
          <div>
            <FileUpload
              buttonText={buttonText}
              label="Choose a file to tee up"
              subtitle={subtitle || `Allowed file extensions: ${accept}`}
              handleUpload={handleFileUpload}
              accept={accept}
              errorMsg={error || undefined}
              variant="filled"
            />
          </div>
        );
      case "fileSelected":
        return <FileDisplay file={selectedFile} status="Ready to upload" error={error} />;
      case "uploading":
        return <FileDisplay file={selectedFile} status="Swinging into Action" error={error} />;
      case "complete":
        return <FileDisplay file={selectedFile} status="Upload complete" />;
      case "error":
        return (
          <div role="alert">
            <p className="text-sys-brand-error">{error}</p>
          </div>
        );
    }
  };

  const renderAction = () => {
    switch (modalState) {
      case "fileSelected":
        return (
          <Button onClick={handleUpload} variant="filled">
            {buttonText}
          </Button>
        );
      case "uploading":
        return (
          <Button
            onClick={handleUpload}
            variant="filled"
            icon="progress_activity"
            iconClassName={classNames("animate-spin w-6 h-6 !block")}
            disabled={true}
          >
            Uploading
          </Button>
        );
      case "complete":
        return onComplete ? (
          <Button onClick={onComplete} variant="filled">
            {completeButtonText}
          </Button>
        ) : null;
      default:
        return null;
    }
  };

  if (!open) return null;

  return (
    <Modal headline={title} action={renderAction()} onClosed={onClose}>
      {description && <div className="pb-8">{description}</div>}
      {renderContent()}
    </Modal>
  );
}
